import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import { decode } from "punycode";

import { Environment } from "../../environment";
import { AlertSnackBar } from "../alert/AlertSnackBar";
import { TJwtToken } from '../../services/api/usuario/auth/AuthService';
import { ClienteService, ICliente } from "../../services/api/cliente/ClienteService";
import { Usuario_Cliente_Service } from "../../services/api/usuario_cliente/Usuario_Cliente_Service";

type AutoCompleteOption = {
  id: string; // apenas algo que diferencie as opções, o valor q vai ser retornado ao escolher
  label: string;
}

interface IAutoComplete {
  /**
   * Deixa o autocomplete desabilitado
   */
  disabled?: boolean,
  /**
   * Fazer com que tenha a indicação que está carregando 
   */
  isExternalLoading?: boolean,
  /**
   * Altera o size do campo (para cadastro o tamanho é médio)
   */
  paraCadastro?: boolean,
  /**
   * Width do campo(padrão 200), null para não ter width e variar de acordo com a tela
   */
  width?: number | null,
  /**
   * Pegar o valor que vai retornar ao trocar opção
   */
  selectedCnpj?: (cnpj: string | undefined | null) => void,
}

/**
 * selectedCnpj={selectedCnpj => setCnpj(selectedCnpj)} \
 * para pegar o valor selecionado e colocar em uma variável/state
 */

export const AutoCompleteDeCliente: React.FC<IAutoComplete> = ({ disabled = false, isExternalLoading = false, paraCadastro = false, width = 230, selectedCnpj }) => {
  useEffect(() => {
    selectedCnpj?.(localStorage.getItem('cnpj'));
  }, [])

  const [selectedId, setSelectedId] = useState<string | null | undefined>(localStorage.getItem('cnpj'));

  const [opcoes, setOpcoes] = useState<AutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(isExternalLoading);

  const [alertSnackbar, setAlertSnackbar] = useState<{ tipoAlert: string, mensagem: string } | undefined>(undefined);
  const [duracaoAlert, setDuracaoAlert] = useState<number>(Environment.DURACAO_ALERT + 500);

  const token = localStorage.getItem("ACESS_TOKEN");
  //@ts-expect-error
  const decoded: TJwtToken = jwt_decode(token);

  const [idUsuario, setIdUsuario] = useState(decoded.name);
  const [visao, setVisao] = useState(decoded.role);

  useEffect(() => {// toda vez que o token for mudado
    const token = localStorage.getItem("ACESS_TOKEN");
    //@ts-expect-error
    const decoded: TJwtToken = jwt_decode(token);
    setIdUsuario(decoded.name);
    setVisao(decoded.role);
  }, [localStorage.getItem("ACESS_TOKEN")]);

  const dataFetchedRef = useRef(false);

  useEffect(() => {// carregar os dados dos clientes
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    if (visao === "master") {
      setIsLoading(true);

      ClienteService.list().then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setAlertSnackbar({ tipoAlert: "erro", mensagem: result.message });
          setTimeout(() => {
            setAlertSnackbar(undefined);
          }, duracaoAlert);
        } else {
          setOpcoes(result.map(cliente => ({ id: cliente.cnpj, label: cliente.nome })));
        }
      });
    } else {
      Usuario_Cliente_Service.getByIdUsuario(idUsuario).then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setAlertSnackbar({ tipoAlert: "erro", mensagem: result.message });
          setTimeout(() => {
            setAlertSnackbar(undefined);
          }, duracaoAlert);
        } else {
          setOpcoes(result.map(cliente => ({ id: cliente.cnpj, label: cliente.nome })));
          if (result.length === 1) {
            selectedCnpj?.(result[0].cnpj);
            setSelectedId(result[0].cnpj);
            localStorage.setItem('cnpj', result[0].cnpj);
          }
        }
      });
    }
  }, [])

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.id === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);


  return (
    <>
      {opcoes.length > 1 && (
        <Autocomplete
          //traduzir textos
          openText="Abrir"
          closeText="Fechar"
          noOptionsText="Sem opções"
          loadingText="Carregando..."


          //estilização
          disablePortal// menu de opções sempre ficar abaixo de onde digita
          disabled={isExternalLoading || isLoading || disabled}
          sx={{ minWidth: width }}
          loading={isLoading}
          popupIcon={isLoading ? <CircularProgress size={20} /> : undefined}//trocar o icone enquanto carrega os dados

          //dados
          options={opcoes}
          onChange={(_, newValue) => {
            selectedCnpj?.(newValue?.id);
            setSelectedId(newValue?.id);
            if (newValue?.id) {
              localStorage.setItem('cnpj', newValue.id);
              localStorage.removeItem('idUnidade');
              localStorage.removeItem('idSetor');
            } else {
              localStorage.setItem('cnpj', '');
              localStorage.removeItem('idUnidade');
              localStorage.removeItem('idSetor');
            }
          }}
          value={autoCompleteSelectedOption}
          renderInput={(params) => <TextField {...params} label="Unidade" placeholder="Digite o nome..." size={paraCadastro ? 'medium' : 'small'} />}
        />
      )}

      {alertSnackbar !== undefined && (
        <>
          {alertSnackbar.tipoAlert === "sucesso" && (
            <AlertSnackBar gravidade="success" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
          )}
          {alertSnackbar.tipoAlert === "erro" && (
            <AlertSnackBar gravidade="error" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
          )}
          {alertSnackbar.tipoAlert === "aviso" && (
            <AlertSnackBar gravidade="warning" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
          )}
        </>
      )}
    </>

  );
}